"use client";

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { SectionTitle } from "@/components/shared/SectionTitle";
import { RarityBadge } from "@/components/shared/RarityBadge";
import { OpenSeaLink } from "@/components/shared/OpenSeaLink";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { CatItem } from "@/types/cat";
import { loadCats } from "@/lib/data/cats";

const featuredIds = [1, 42, 88, 156, 234, 333, 444, 555];

export function FeaturedCats() {
  const [cats, setCats] = useState<CatItem[]>([]);
  const [selectedCat, setSelectedCat] = useState<CatItem | null>(null);

  useEffect(() => {
    loadCats().then((allCats) => {
      const featured = featuredIds
        .map((id) => allCats.find((c) => c.id === id))
        .filter(Boolean) as CatItem[];
      setCats(featured);
    });
  }, []);

  return (
    <section className="bg-card/30 py-20 md:py-28">
      <div className="container mx-auto px-4">
        <SectionTitle
          subtitle="PREVIEW"
          title="Featured Cats"
          description="Every Cat Claw is a one-of-a-kind pixel art piece"
          centered
        />

        <div className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-4">
          {cats.map((cat, index) => (
            <motion.div
              key={cat.id}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              className="group relative aspect-square cursor-pointer overflow-hidden rounded-xl border border-border bg-card"
              onClick={() => setSelectedCat(cat)}
            >
              <img
                src={cat.image}
                alt={cat.name}
                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 transition-opacity group-hover:opacity-100">
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <p className="font-pixel text-xs text-white">{cat.name}</p>
                  <div className="mt-2">
                    <RarityBadge rarity={cat.rarity} />
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 text-center">
          <Link
            to="/collection"
            className="inline-flex h-12 items-center rounded-xl bg-primary px-8 font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            View all 1,024 cats
          </Link>
        </div>

        <Dialog
          open={!!selectedCat}
          onOpenChange={() => setSelectedCat(null)}
        >
          <DialogContent className="max-w-md border-border bg-card">
            <DialogTitle className="sr-only">
              {selectedCat?.name || "Cat Details"}
            </DialogTitle>
            {selectedCat && (
              <div>
                <div className="relative aspect-square overflow-hidden rounded-xl">
                  <img
                    src={selectedCat.image}
                    alt={selectedCat.name}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="mt-4">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="text-xl font-bold">{selectedCat.name}</h3>
                    <OpenSeaLink tokenId={selectedCat.id} className="shrink-0" />
                  </div>
                  <div className="mt-2">
                    <RarityBadge rarity={selectedCat.rarity} />
                  </div>
                  <p className="mt-3 text-sm text-muted-foreground">
                    {selectedCat.description}
                  </p>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
}
